/*
 * ReelOwnerMenu — Phase 1D (RGM Reels)
 * ====================================
 * Context menu shown ONLY to the Reel owner (or admin). Lets them:
 *   • Edit the caption (PATCH /api/reels/{reel_id})
 *   • Delete the Reel (DELETE /api/reels/{reel_id}) — requires an
 *     explicit "Yes, delete" confirm step so a mis-tap can't nuke
 *     their drop. Underlying media deletion is handled server-side.
 *
 * The underlying media is NOT editable in Phase 1D — only the
 * caption. If the owner wants a different clip they must re-record /
 * re-upload.
 */
import { useState, useEffect } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

const ReelOwnerMenu = ({ reel, onUpdated, onDeleted }) => {
  const [open, setOpen] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [caption, setCaption] = useState(reel?.caption || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setCaption(reel?.caption || ""); }, [reel?.reel_id, reel?.caption]);

  const saveCaption = async () => {
    setBusy(true);
    try {
      const r = await api.patch(`/reels/${reel.reel_id}`, { caption });
      toast.success("Caption updated");
      setShowEdit(false);
      setOpen(false);
      if (typeof onUpdated === "function") onUpdated(r.data?.reel || { ...reel, caption });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update caption");
    } finally {
      setBusy(false);
    }
  };

  // Phase 1E — Owner-only download. Opens the backend export route
  // in a new tab/window; the server resolves the signed Cloudinary
  // URL (watermark + fl_attachment) and 302-redirects. On iOS Safari
  // a tap on a direct MP4 URL offers Save-to-Files / share to
  // Photos via the native share sheet, which is the most reliable
  // path for cross-app repost (Instagram, TikTok, Facebook,
  // Telegram) — a raw Cloudinary URL is accepted by all.
  const handleDownload = async () => {
    setBusy(true);
    try {
      // Backend returns JSON { download_url, filename }. We intentionally
      // do NOT use a 302 redirect here — in-browser axios can't disable
      // redirect-following, and the cross-origin follow-up to Cloudinary
      // sometimes trips iPhone Safari CORS, which previously surfaced as
      // a generic "Couldn't prepare the download" toast even when the
      // signed Cloudinary URL itself was valid.
      const r = await api.get(`/reels/${reel.reel_id}/export`);
      const downloadUrl = r?.data?.download_url;
      if (!downloadUrl) {
        toast.error("Couldn't prepare the download");
        return;
      }
      setOpen(false);
      // Direct navigation to the signed Cloudinary MP4 URL. iOS Safari
      // shows the native Save-to-Files / Save-to-Photos / Share sheet
      // because the asset carries ``fl_attachment:rgm-reel-<id>``
      // (Content-Disposition: attachment). On desktop browsers the
      // file downloads straight into the Downloads folder.
      window.location.href = downloadUrl;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (detail && typeof detail === "object" && detail.message)
        || (typeof detail === "string" && detail)
        || "Couldn't prepare the download";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/reels/${reel.reel_id}`);
      toast.success("Reel deleted");
      setShowDelete(false);
      setOpen(false);
      if (typeof onDeleted === "function") onDeleted(reel.reel_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete Reel");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="text-white bg-black/50 rounded-full w-8 h-8 flex items-center justify-center text-base"
        aria-label="More options"
        data-testid={`reel-owner-menu-${reel.reel_id}`}
      >⋯</button>
      {open && (
        <div
          className="fixed inset-0 z-[80] flex items-end sm:items-center sm:justify-center"
          onClick={() => setOpen(false)}
          data-testid={`reel-owner-menu-open-${reel.reel_id}`}
        >
          <div className="absolute inset-0 bg-black/60" />
          <div
            className="relative w-full sm:w-[420px] bg-[#111018] border-t sm:border border-purple-500/30 sm:rounded-xl p-2"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => { setShowEdit(true); setOpen(false); }}
              className="w-full text-left px-4 py-3 text-sm text-white hover:bg-white/5 rounded"
              data-testid={`reel-owner-edit-${reel.reel_id}`}
            >✏️ Edit caption</button>
            <button
              type="button"
              onClick={handleDownload}
              disabled={busy}
              className="w-full text-left px-4 py-3 text-sm text-white hover:bg-white/5 rounded disabled:opacity-50"
              data-testid={`reel-owner-download-${reel.reel_id}`}
            >{busy ? "⬇️ Preparing download…" : "⬇️ Download / Save Video"}</button>
            <button
              type="button"
              onClick={() => { setShowDelete(true); setOpen(false); }}
              className="w-full text-left px-4 py-3 text-sm text-red-400 hover:bg-red-500/10 rounded"
              data-testid={`reel-owner-delete-${reel.reel_id}`}
            >🗑 Delete Reel</button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="w-full text-center px-4 py-3 text-xs text-gray-500 hover:text-white mt-1"
            >Cancel</button>
          </div>
        </div>
      )}
      {showEdit && (
        <div className="fixed inset-0 z-[85] flex items-center justify-center p-4" data-testid={`reel-edit-modal-${reel.reel_id}`}>
          <div className="absolute inset-0 bg-black/70" onClick={() => setShowEdit(false)} />
          <div className="relative w-full max-w-sm bg-[#121018] border border-purple-500/30 rounded-xl p-4">
            <div className="text-xs uppercase tracking-widest text-purple-300 mb-1">Edit caption</div>
            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value.slice(0, 300))}
              rows={4}
              className="w-full bg-black/50 border border-gray-700 rounded px-3 py-2 text-white text-sm min-h-[90px] resize-none"
              placeholder="Caption"
              data-testid={`reel-edit-caption-input-${reel.reel_id}`}
            />
            <div className="flex items-center justify-between text-xs text-gray-500 mt-1">
              <span>{caption.length}/300</span>
            </div>
            <div className="flex gap-2 mt-3">
              <Button
                variant="outline"
                className="flex-1 text-white border-gray-700"
                onClick={() => setShowEdit(false)}
                disabled={busy}
              >Cancel</Button>
              <Button
                className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500"
                onClick={saveCaption}
                disabled={busy}
                data-testid={`reel-edit-save-${reel.reel_id}`}
              >{busy ? "Saving…" : "Save"}</Button>
            </div>
          </div>
        </div>
      )}
      {showDelete && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" data-testid={`reel-delete-modal-${reel.reel_id}`}>
          <div className="absolute inset-0 bg-black/80" onClick={() => setShowDelete(false)} />
          <div className="relative w-full max-w-sm bg-[#121018] border border-red-500/40 rounded-xl p-4">
            <div className="text-sm text-white font-black mb-1">Delete this Reel?</div>
            <p className="text-xs text-gray-400 mb-4">
              This will permanently remove the Reel and its uploaded media. This cannot be undone.
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1 text-white border-gray-700"
                onClick={() => setShowDelete(false)}
                disabled={busy}
              >Cancel</Button>
              <Button
                className="flex-1 bg-red-600 hover:bg-red-500"
                onClick={confirmDelete}
                disabled={busy}
                data-testid={`reel-delete-confirm-${reel.reel_id}`}
              >{busy ? "Deleting…" : "Yes, delete"}</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ReelOwnerMenu;
